// src/pages/InventarioPage.tsx
import { useEffect, useState } from 'react'
import api from '../services/api'
import { Search, Warehouse, AlertTriangle, Package } from 'lucide-react'

interface ProductoInventario {
  id_producto: number
  detalle: string
  codigo_barras: string
  stock_actual: number
  precio_venta_a: number
  precio_venta_b: number
  precio_venta_c: number
  iva: string
}

interface AlertaStock {
  id_producto: number
  detalle: string
  codigo_barras: string
  stock_actual: number
  nivel_alerta: string
}

export function InventarioPage() {
  const [productos, setProductos] = useState<ProductoInventario[]>([])
  const [alertas, setAlertas] = useState<AlertaStock[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showBajoStock, setShowBajoStock] = useState(false)

  const fetchData = async () => {
    setLoading(true)
    try {
      const [invRes, alertasRes] = await Promise.all([
        api.get(`/inventario?bajo_stock=${showBajoStock}`),
        api.get('/inventario/alertas')
      ])
      setProductos(invRes.data.data || [])
      setAlertas(alertasRes.data.alertas || [])
    } catch (error) {
      console.error('Error cargando inventario:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [showBajoStock])

  const productosFiltrados = productos.filter(p =>
    p.detalle.toLowerCase().includes(search.toLowerCase()) ||
    p.codigo_barras?.toLowerCase().includes(search.toLowerCase())
  )

  const getStockClass = (stock: number) => {
    if (stock <= 0) return 'bg-red-100 text-red-700'
    if (stock <= 5) return 'bg-orange-100 text-orange-700'
    if (stock <= 10) return 'bg-yellow-100 text-yellow-700'
    return 'bg-green-100 text-green-700'
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Inventario</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center">
            <div className="bg-blue-500 p-3 rounded-lg">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Total Productos</p>
              <p className="text-2xl font-bold text-gray-800">{productos.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center">
            <div className="bg-green-500 p-3 rounded-lg">
              <Warehouse className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Con Stock Normal</p>
              <p className="text-2xl font-bold text-gray-800">
                {productos.filter(p => p.stock_actual > 10).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center">
            <div className={`${alertas.length > 0 ? 'bg-red-500' : 'bg-gray-400'} p-3 rounded-lg`}>
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">Alertas de Stock</p>
              <p className="text-2xl font-bold text-gray-800">{alertas.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alertas */}
      {alertas.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center mb-3">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
            <h3 className="font-semibold text-red-800">Productos con Stock Bajo</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {alertas.slice(0, 6).map(alerta => (
              <div key={alerta.id_producto} className="bg-white rounded-lg p-3 border border-red-200">
                <p className="font-medium text-gray-800">{alerta.detalle}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-sm text-gray-500">Stock: {alerta.stock_actual}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    alerta.nivel_alerta === 'SIN_STOCK'
                      ? 'bg-red-100 text-red-700'
                      : alerta.nivel_alerta === 'CRITICO'
                      ? 'bg-orange-100 text-orange-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {alerta.nivel_alerta}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar producto..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showBajoStock}
              onChange={(e) => setShowBajoStock(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">Solo bajo stock</span>
          </label>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : productosFiltrados.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <Warehouse className="w-12 h-12 mb-4" />
            <p>No hay productos en el inventario</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Stock</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">P. Venta A</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">P. Venta B</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">P. Venta C</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {productosFiltrados.map((producto) => (
                  <tr key={producto.id_producto} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="font-medium">{producto.detalle}</p>
                      <p className="text-xs text-gray-500">IVA {producto.iva}%</p>
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {producto.codigo_barras || '-'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStockClass(producto.stock_actual)}`}>
                        {producto.stock_actual}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right font-medium">
                      ${producto.precio_venta_a?.toFixed(2) || '0.00'}
                    </td>
                    <td className="px-6 py-4 text-right text-gray-500">
                      ${producto.precio_venta_b?.toFixed(2) || '0.00'}
                    </td>
                    <td className="px-6 py-4 text-right text-gray-500">
                      ${producto.precio_venta_c?.toFixed(2) || '0.00'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
